import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, TextInput, SelectDropdown, Modal } from "../ui";
import {
  generateUnits,
  getTowerConfigs,
  getSocietyUnitTypes,
  addSocietyUnitType,
} from "../../services/societyService";
import {
  getFlatsBySociety,
  updateFlatStructure,
} from "../../services/flatsService";
import {
  Pencil,
  X,
  Plus,
  Trash2,
  Building,
  Building2,
  Layers,
  LayoutGrid,
  Shield,
  ChevronDown,
  ChevronRight,
  Check,
  ArrowDownToLine,
  Lock,
} from "lucide-react";
import {
  generateWorkspaceCode,
  generateBulkWorkspaceCodes,
} from "../../utils/codeGenerator";
import { unitGenerationSchema, zodErrorToFirstMessage } from "../../schemas/formSchemas";
import {
  getStructureConfigByPropertyType,
  formatFloorName,
} from "../../utils/propertyStructure";
import styles from "./UnitForm.module.css";

const MAX_UNITS = 100;

const UnitForm = ({ towers = [], societyId, propertyName = '', propertyType, readOnly, onSuccess, initialFloor = null, initialDepartment = "" }) => {
  const [configs, setConfigs] = useState([]);
  const [activeTowerId, setActiveTowerId] = useState(null);
  const [editableFloors, setEditableFloors] = useState({});
  const [originalFloorSnapshot, setOriginalFloorSnapshot] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedFloors, setExpandedFloors] = useState({}); // {towerId: {floorNo: true/false}}
  const [floorToDelete, setFloorToDelete] = useState(null); // { towerId, floorNumber, unitCount }
  const [deletingFloor, setDeletingFloor] = useState(false);
  const [savingFloorKey, setSavingFloorKey] = useState(null); // `${towerId}_${floor}`

  // Workspace Modal (Add Cabins / Desks with Bulk Toggle)
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [targetFloorForWorkspace, setTargetFloorForWorkspace] = useState(null); // { tIndex, fIndex, floorNumber }
  const [workspaceType, setWorkspaceType] = useState("desk"); // "desk" | "cabin"
  const [bulkMode, setBulkMode] = useState(false); // Bulk generation toggle: ON/OFF
  const [workspacePayload, setWorkspacePayload] = useState({
    name: "",
    count: 4,
    prefix: "D-",
    desksPerCabin: 1,
    seatsPerDesk: 1,
    department: "",
  });

  const existingPropertyCodes = useMemo(() => {
    return configs.flatMap((t) =>
      (t.floorConfigs || []).flatMap((f) => (f.units || []).map((u) => u.number))
    );
  }, [configs]);

  const effectivePropertyName = propertyName || configs[0]?.tower_name || "";

  const livePreviewCode = useMemo(() => {
    return generateWorkspaceCode({
      propertyName: effectivePropertyName,
      department: workspacePayload.department,
      type: workspaceType,
      existingCodes: existingPropertyCodes,
    });
  }, [effectivePropertyName, workspacePayload.department, workspaceType, existingPropertyCodes]);

  const livePreviewRange = useMemo(() => {
    const count = bulkMode ? Math.max(1, Number(workspacePayload.count) || 1) : 1;
    const codes = generateBulkWorkspaceCodes({
      propertyName: effectivePropertyName,
      department: workspacePayload.department,
      type: workspaceType,
      count,
      existingCodes: existingPropertyCodes,
    });
    if (codes.length <= 1) return codes[0] || "";
    return `${codes[0]} to ${codes[codes.length - 1]} (${codes.length} total)`;
  }, [effectivePropertyName, workspacePayload.department, workspaceType, bulkMode, workspacePayload.count, existingPropertyCodes]);

  // Dynamic property structure config & unit types
  const structureConfig = useMemo(
    () => getStructureConfigByPropertyType(propertyType),
    [propertyType]
  );

  const shortUnitLabel = useMemo(() => {
    const raw = structureConfig.unitLabel || "Unit";
    return raw.includes("/") ? raw.split("/")[0].trim() : raw;
  }, [structureConfig.unitLabel]);

  const shortUnitPlural = useMemo(() => {
    const raw = structureConfig.unitPlural || "Units";
    return raw.includes("/") ? raw.split("/")[0].trim() : raw;
  }, [structureConfig.unitPlural]);

  const isIndustrial = useMemo(() => {
    return (
      String(structureConfig?.category || "").toUpperCase() === "INDUSTRIAL" ||
      String(propertyType || "").toLowerCase().includes("industrial")
    );
  }, [structureConfig, propertyType]);

  const [defaultUnitType, setDefaultUnitType] = useState(
    isIndustrial ? "" : (structureConfig.defaultUnitType || "1BHK")
  );
  const [unitOptions, setUnitOptions] = useState(structureConfig.standardUnitTypes || ["1BHK", "2BHK", "3BHK"]);
  const [customTypeInput, setCustomTypeInput] = useState("");
  const [showCustomTypeInput, setShowCustomTypeInput] = useState(false);
  const [savingCustomType, setSavingCustomType] = useState(false);

  useEffect(() => {
    if (!isIndustrial && structureConfig.defaultUnitType) {
      setDefaultUnitType(structureConfig.defaultUnitType);
    }
  }, [structureConfig.defaultUnitType, isIndustrial]);

  // Fetch dynamic unit types from server for this society
  const fetchUnitTypes = useCallback(async () => {
    if (!societyId) return;
    try {
      const data = await getSocietyUnitTypes(societyId);
      if (data?.unit_types && Array.isArray(data.unit_types) && data.unit_types.length) {
        setUnitOptions(data.unit_types);
        if (!isIndustrial) {
          if (data.default_type) {
            setDefaultUnitType(data.default_type);
          } else if (data.unit_types[0]) {
            setDefaultUnitType(data.unit_types[0]);
          }
        }
      }
    } catch {
      // fallback already in place
    }
  }, [societyId]);

  useEffect(() => {
    fetchUnitTypes();
  }, [fetchUnitTypes]);

  const handleAddCustomUnitType = async () => {
    const trimmed = customTypeInput.trim();
    if (!trimmed) return;
    if (unitOptions.includes(trimmed)) {
      toast.info(`"${trimmed}" is already an available unit type.`);
      setShowCustomTypeInput(false);
      setCustomTypeInput("");
      return;
    }

    try {
      setSavingCustomType(true);
      if (societyId) {
        await addSocietyUnitType(societyId, trimmed);
      }
      setUnitOptions((prev) => Array.from(new Set([trimmed, ...prev])));
      setCustomTypeInput("");
      setShowCustomTypeInput(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingCustomType(false);
    }
  };

  const isEditable = (towerId, floorNo) =>
    !!editableFloors?.[towerId]?.[Number(floorNo)];

  const isExpanded = (towerId, floorNo) =>
    !!expandedFloors?.[towerId]?.[Number(floorNo)];

  const toggleFloorExpand = (towerId, floorNo) => {
    setExpandedFloors((prev) => {
      const next = { ...prev };
      const towerMap = { ...(next[towerId] || {}) };
      towerMap[Number(floorNo)] = !towerMap[Number(floorNo)];
      next[towerId] = towerMap;
      return next;
    });
  };

  const addFloor = (tIndex, specificFloorNumber = null) => {
    const tower = configs[tIndex];
    if (!tower) return;
    const existingFloors = tower.floorConfigs.map((f) => f.floor);
    let targetFloor;

    if (specificFloorNumber !== null && Number.isInteger(specificFloorNumber)) {
      if (existingFloors.includes(specificFloorNumber)) {
        toast.warn(`${formatFloorName(specificFloorNumber)} already exists.`);
        return;
      }
      targetFloor = specificFloorNumber;
    } else {
      const maxFloor = existingFloors.length > 0
        ? Math.max(...existingFloors)
        : (structureConfig.defaultStartFloor === 0 ? -1 : 0);
      targetFloor = maxFloor >= 0 ? maxFloor + 1 : 0;
    }

    const newFloor = {
      floor: targetFloor,
      units: [],
      unitCount: 0,
      defaultType: defaultUnitType,
    };

    setConfigs((prev) =>
      prev.map((t, i) => {
        if (i !== tIndex) return t;
        return {
          ...t,
          floorConfigs: [...t.floorConfigs, newFloor].sort((a, b) => a.floor - b.floor),
        };
      })
    );

    // Automatically expand and enter edit mode so user sees Add Workspaces & Save Floor Changes immediately
    setExpandedFloors((prev) => ({
      ...prev,
      [tower.tower_id]: {
        ...(prev[tower.tower_id] || {}),
        [targetFloor]: true,
      },
    }));

    setEditableFloors((prev) => ({
      ...prev,
      [tower.tower_id]: {
        ...(prev[tower.tower_id] || {}),
        [targetFloor]: true,
      },
    }));

    toast.info(`${formatFloorName(targetFloor)} added. Add workspaces or click "Save Floor Changes".`);
  };

  /* ================= INIT ================= */
  useEffect(() => {
    if (!towers?.length) return;
    setActiveTowerId(towers[0]?.tower_id);
  }, [towers]);

  /* ================= LOAD EXISTING ================= */
  const loadConfigs = useCallback(async () => {
    if (!societyId || !towers.length) return;

    try {
      const [towerConfigs, flats] = await Promise.all([
        getTowerConfigs(societyId),
        getFlatsBySociety(societyId),
      ]);

      const map = {};

      flats.forEach((f) => {
        const tId = f.tower_id;
        const floor = Number(f.floor_number);

        if (!map[tId]) map[tId] = {};
        if (!map[tId][floor]) map[tId][floor] = [];

        map[tId][floor].push({
          flat_id: f.flat_id,
          number: String(f.flat_number),
          type: f.unit_type || defaultUnitType,
        });
      });

      // For residential properties, include any custom in-use types; for industrial/commercial, keep master department taxonomy authoritative
      const categoryUpper = String(structureConfig?.category || "").toUpperCase();
      const isIndOrComm = categoryUpper === "INDUSTRIAL" || categoryUpper === "COMMERCIAL";
      if (!isIndOrComm) {
        const flatTypes = new Set(flats.map((f) => f.unit_type?.trim()).filter(Boolean));
        if (flatTypes.size > 0) {
          setUnitOptions((prev) => Array.from(new Set([...prev, ...flatTypes])));
        }
      }

      setConfigs(
        towers.map((t) => {
          const config = Array.isArray(towerConfigs)
            ? towerConfigs.find((tc) => tc.tower_id === t.tower_id)
            : null;

          const floors = map[t.tower_id] || {};

          // If tower has floor records from backend config, ensure they exist in map
          if (Array.isArray(config?.floors)) {
            config.floors.forEach((fNum) => {
              const fn = Number(typeof fNum === "object" ? fNum.floor_number : fNum);
              if (Number.isInteger(fn) && !floors[fn]) {
                floors[fn] = [];
              }
            });
          }

          const floorConfigs = Object.keys(floors)
            .map((floorNo) => {
              const units = floors[floorNo];
              return {
                floor: Number(floorNo),
                units,
                unitCount: units.length,
                defaultType:
                  units.length && units.every((u) => u.type === units[0].type)
                    ? units[0].type
                    : defaultUnitType,
              };
            })
            .sort((a, b) => a.floor - b.floor);

          return {
            tower_id: t.tower_id,
            tower_name: t.tower_name,
            total_floors: config?.total_floors || "",
            units_per_floor: config?.units_per_floor || "",
            start_floor: config?.start_floor ?? structureConfig.defaultStartFloor ?? 1,
            default_unit_type: defaultUnitType,
            floorConfigs,
            hasExistingUnits: floorConfigs.length > 0,
          };
        })
      );
    } catch (err) {
      console.error("LOAD CONFIGS ERROR:", err);
    }
  }, [societyId, towers, defaultUnitType, structureConfig.defaultStartFloor]);

  /* initialFloorEffectAdded */

  useEffect(() => {
    if (initialFloor !== null && initialFloor !== undefined && activeTowerId) {
      const flNum = Number(initialFloor);
      setExpandedFloors((prev) => ({
        ...prev,
        [activeTowerId]: {
          ...(prev[activeTowerId] || {}),
          [flNum]: true,
        },
      }));
      setEditableFloors((prev) => ({
        ...prev,
        [activeTowerId]: {
          ...(prev[activeTowerId] || {}),
          [flNum]: true,
        },
      }));
    }
  }, [initialFloor, activeTowerId]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  /* ================= EDIT TOGGLE ================= */
  const toggleFloorEdit = (towerId, floorNo, tIndex, fIndex) => {
    const fNo = Number(floorNo);

    setEditableFloors((prev) => {
      const next = { ...prev };
      const towerMap = { ...(next[towerId] || {}) };
      const isEditing = towerMap[fNo];

      if (!isEditing) {
        const snapshot = configs[tIndex].floorConfigs[fIndex];

        setOriginalFloorSnapshot((prevSnap) => ({
          ...prevSnap,
          [towerId]: {
            ...(prevSnap[towerId] || {}),
            [fNo]: JSON.parse(JSON.stringify(snapshot)),
          },
        }));
      } else {
        const snap = originalFloorSnapshot?.[towerId]?.[fNo];

        if (snap) {
          setConfigs((prev) =>
            prev.map((t) =>
              t.tower_id === towerId
                ? {
                  ...t,
                  floorConfigs: t.floorConfigs.map((f) =>
                    f.floor === fNo ? snap : f
                  ),
                }
                : t
            )
          );
        }
      }

      towerMap[fNo] = !isEditing;
      next[towerId] = towerMap;
      return next;
    });
  };

  /* ================= UNIT COUNT ================= */
  const handleUnitCountChange = (tIndex, fIndex, value) => {
    const count = Math.min(Number(value || 0), MAX_UNITS);

    setConfigs((prev) =>
      prev.map((t, i) => {
        if (i !== tIndex) return t;

        return {
          ...t,
          floorConfigs: t.floorConfigs.map((f, idx) => {
            if (idx !== fIndex) return f;

            const units = [...f.units];

            if (count > units.length) {
              const extra = Array.from(
                { length: count - units.length },
                (_, j) => {
                  const uNum = units.length + j + 1;
                  const numStr =
                    f.floor === 0
                      ? `G${String(uNum).padStart(2, "0")}`
                      : f.floor < 0
                        ? `B${Math.abs(f.floor)}${String(uNum).padStart(2, "0")}`
                        : `${f.floor}${String(uNum).padStart(2, "0")}`;

                  return {
                    number: numStr,
                    type: f.defaultType || defaultUnitType,
                  };
                }
              );
              return { ...f, unitCount: count, units: [...units, ...extra] };
            }

            return { ...f, unitCount: count, units: units.slice(0, count) };
          }),
        };
      })
    );
  };

  /* ================= TYPE CHANGE ================= */
  const handleFloorTypeChange = (tIndex, fIndex, value) => {
    setConfigs((prev) =>
      prev.map((t, i) => {
        if (i !== tIndex) return t;

        return {
          ...t,
          floorConfigs: t.floorConfigs.map((f, idx) => {
            if (idx !== fIndex) return f;
            const oldType = f.defaultType;
            return {
              ...f,
              defaultType: value,
              units: f.units.map((u) => (!u.type || u.type === oldType ? { ...u, type: value } : u)),
            };
          }),
        };
      })
    );
  };

  const handleAddUnit = (tIndex, fIndex) => {
    setConfigs((prev) =>
      prev.map((t, i) => {
        if (i !== tIndex) return t;
        return {
          ...t,
          floorConfigs: t.floorConfigs.map((f, idx) => {
            if (idx !== fIndex) return f;
            const uNum = f.units.length + 1;
            const numStr =
              f.floor === 0
                ? `G${String(uNum).padStart(2, "0")}`
                : f.floor < 0
                  ? `B${Math.abs(f.floor)}${String(uNum).padStart(2, "0")}`
                  : `${f.floor}${String(uNum).padStart(2, "0")}`;
            const newUnits = [
              ...f.units,
              { number: numStr, type: f.defaultType || defaultUnitType },
            ];
            return { ...f, unitCount: newUnits.length, units: newUnits };
          }),
        };
      })
    );
  };

  const handleRemoveUnit = (tIndex, fIndex, uIndex) => {
    setConfigs((prev) =>
      prev.map((t, i) => {
        if (i !== tIndex) return t;
        return {
          ...t,
          floorConfigs: t.floorConfigs.map((f, idx) => {
            if (idx !== fIndex) return f;
            const newUnits = f.units.filter((_, ui) => ui !== uIndex);
            return { ...f, unitCount: newUnits.length, units: newUnits };
          }),
        };
      })
    );
  };

  const handleUnitChange = (tIndex, fIndex, uIndex, value) => {
    setConfigs((prev) =>
      prev.map((t, i) => {
        if (i !== tIndex) return t;

        const currentFloor = t.floorConfigs[fIndex];
        const currentUnit = currentFloor?.units?.[uIndex];
        let newNumber = currentUnit?.number;

        if (isIndustrial && value && value !== "—" && value !== "Desk" && value !== "Cabin") {
          const isCabin =
            String(currentUnit?.number || "").includes("-c") ||
            String(currentUnit?.number || "").includes("C-") ||
            String(currentUnit?.type || "").toLowerCase().includes("cabin");

          const allOtherCodes = prev.flatMap((tw, twIdx) =>
            (tw.floorConfigs || []).flatMap((fl, flIdx) =>
              (fl.units || [])
                .filter((_, unIdx) => !(twIdx === tIndex && flIdx === fIndex && unIdx === uIndex))
                .map((u) => u.number)
            )
          );

          newNumber = generateWorkspaceCode({
            propertyName: effectivePropertyName,
            department: value,
            type: isCabin ? "cabin" : "desk",
            existingCodes: allOtherCodes,
          });
        }

        return {
          ...t,
          floorConfigs: t.floorConfigs.map((f, idx) =>
            idx === fIndex
              ? {
                ...f,
                units: f.units.map((u, ui) =>
                  ui === uIndex
                    ? {
                        ...u,
                        type: value,
                        number: newNumber || u.number,
                      }
                    : u
                ),
              }
              : f
          ),
        };
      })
    );
  };

  /* ================= WORKSPACE GENERATOR / APPEND HANDLER ================= */
  const openWorkspaceModal = (tIndex, fIndex, floorNumber) => {
    setTargetFloorForWorkspace({ tIndex, fIndex, floorNumber });
    setWorkspaceType("desk");
    setBulkMode(false);
    const floor = configs[tIndex]?.floorConfigs[fIndex];
    const validDepts = unitOptions.filter(
      (o) => o && o !== "—" && o !== "Desk" && o !== "Cabin" && o !== "Unit" && o !== "Flat" && o !== "None"
    );
    const defaultDept =
      initialDepartment && validDepts.includes(initialDepartment)
        ? initialDepartment
        : floor?.defaultType && validDepts.includes(floor.defaultType)
          ? floor.defaultType
          : (validDepts[0] || "HR");

    setWorkspacePayload({
      name: "",
      count: 4,
      prefix: "D-",
      desksPerCabin: 1,
      seatsPerDesk: 1,
      department: defaultDept,
    });
    setWorkspaceModalOpen(true);
  };

  const handleApplyWorkspaces = () => {
    if (!targetFloorForWorkspace) return;
    const { tIndex, fIndex, floorNumber } = targetFloorForWorkspace;
    const tower = configs[tIndex];
    const floor = tower.floorConfigs[fIndex];
    if (!floor) return;

    const deptTag = workspacePayload.department?.trim() || "";
    if (isIndustrial && !deptTag) {
      toast.error("Please select a department. Desks and cabins cannot be added without assigning a department.");
      return;
    }

    const existingUnits = [...floor.units];
    const newGeneratedUnits = [];
    const flNum = Number(floorNumber);
    const flPrefix = flNum === 0 ? "G" : flNum < 0 ? `B${Math.abs(flNum)}` : `${flNum}`;

    if (workspaceType === "cabin") {
      // Adding Cabins
      const cabinCount = bulkMode ? Math.max(1, Number(workspacePayload.count) || 1) : 1;
      const desksPerCab = Math.max(1, Number(workspacePayload.desksPerCabin) || 1);
      const seatsPerD = Math.max(1, Number(workspacePayload.seatsPerDesk) || 1);
      const startIdx = existingUnits.filter(u => String(u.number).includes("C-") || String(u.number).toLowerCase().includes("cabin")).length + 1;

      for (let c = 0; c < cabinCount; c++) {
        const cNum = startIdx + c;
        const cabinCode = !bulkMode && workspacePayload.name.trim()
          ? workspacePayload.name.trim()
          : `${workspacePayload.prefix || "C-"}${flPrefix}${String(cNum).padStart(2, "0")}`;

        for (let d = 0; d < desksPerCab; d++) {
          const deskCode = desksPerCab === 1 ? `${cabinCode}` : `${cabinCode}-D${d + 1}`;
          for (let s = 0; s < seatsPerD; s++) {
            const seatCode = seatsPerD === 1 ? deskCode : `${deskCode}-S${s + 1}`;
            newGeneratedUnits.push({
              number: seatCode,
              type: deptTag || "Cabin",
            });
          }
        }
      }
    } else {
      // Adding Open Floor Desks (No Cabins!)
      const deskCount = bulkMode ? Math.max(1, Number(workspacePayload.count) || 1) : 1;
      const seatsPerD = Math.max(1, Number(workspacePayload.seatsPerDesk) || 1);
      const startIdx = existingUnits.filter(u => !String(u.number).includes("C-") && !String(u.number).toLowerCase().includes("cabin")).length + 1;

      for (let d = 0; d < deskCount; d++) {
        const dNum = startIdx + d;
        const deskCode = !bulkMode && workspacePayload.name.trim()
          ? workspacePayload.name.trim()
          : `${workspacePayload.prefix || "D-"}${flPrefix}${String(dNum).padStart(2, "0")}`;

        for (let s = 0; s < seatsPerD; s++) {
          const seatCode = seatsPerD === 1 ? deskCode : `${deskCode}-S${s + 1}`;
          newGeneratedUnits.push({
            number: seatCode,
            type: deptTag || "Desk",
          });
        }
      }
    }

    const mergedUnits = [...existingUnits, ...newGeneratedUnits];

    setConfigs((prev) =>
      prev.map((t, i) =>
        i !== tIndex
          ? t
          : {
            ...t,
            floorConfigs: t.floorConfigs.map((f, idx) =>
              idx !== fIndex
                ? f
                : {
                  ...f,
                  unitCount: mergedUnits.length,
                  units: mergedUnits,
                }
            ),
          }
      )
    );

    // Auto-expand and enable edit mode for this floor so changes are immediately visible and saveable
    setExpandedFloors((prev) => ({
      ...prev,
      [tower.tower_id]: {
        ...(prev[tower.tower_id] || {}),
        [flNum]: true,
      },
    }));

    setEditableFloors((prev) => ({
      ...prev,
      [tower.tower_id]: {
        ...(prev[tower.tower_id] || {}),
        [flNum]: true,
      },
    }));

    setWorkspaceModalOpen(false);
    toast.success(`Added ${newGeneratedUnits.length} workspace item(s) to ${formatFloorName(flNum)}. Click "Save Floor Changes" when done.`);
  };

  const handleTowerConfigChange = (tIndex, key, value) => {
    setConfigs((prev) =>
      prev.map((t, i) =>
        i !== tIndex
          ? t
          : {
            ...t,
            [key]: value,
          }
      )
    );
  };

  const isGenerateValid = (tower) => {
    if (isIndustrial) {
      return Boolean(tower?.tower_id && Number(tower?.total_floors) > 0);
    }
    return unitGenerationSchema.safeParse({
      tower_id: tower.tower_id,
      total_floors: tower.total_floors,
      units_per_floor: tower.units_per_floor,
    }).success;
  };

  const handleGenerateTowerUnits = async (tIndex) => {
    const tower = configs[tIndex];
    if (isIndustrial) {
      if (!tower.tower_id || !Number(tower.total_floors) || Number(tower.total_floors) <= 0) {
        toast.error("Please enter valid total floors");
        return;
      }
    } else {
      const parsed = unitGenerationSchema.safeParse({
        tower_id: tower.tower_id,
        total_floors: tower.total_floors,
        units_per_floor: tower.units_per_floor,
      });

      if (!parsed.success) {
        toast.error(zodErrorToFirstMessage(parsed.error));
        return;
      }
    }

    try {
      setLoading(true);
      await generateUnits({
        configs: [
          {
            tower_id: Number(tower.tower_id),
            total_floors: Number(tower.total_floors),
            units_per_floor: Number(tower.units_per_floor || 0),
            start_floor: Number.isInteger(Number(tower.start_floor))
              ? Number(tower.start_floor)
              : structureConfig.defaultStartFloor ?? 1,
            default_unit_type: tower.default_unit_type || defaultUnitType || "—",
          },
        ],
      });

      toast.success(isIndustrial ? "Workspaces initialized successfully" : "Units generated successfully");
      await loadConfigs();
      onSuccess?.();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectDeleteUnit = async (towerId, floorNumber, unit, configIndex, fIndex, uIndex) => {
    if (!window.confirm(`Are you sure you want to remove workspace "${unit.number}"?`)) return;
    try {
      if (unit.flat_id) {
        await updateFlatStructure([{ action: "REMOVE", flat_id: unit.flat_id }]);
      } else {
        const tower = configs[configIndex];
        const floor = tower.floorConfigs[fIndex];
        const remaining = floor.units.filter((_, idx) => idx !== uIndex);
        await updateFlatStructure({
          tower_id: towerId,
          floor_number: floorNumber,
          units: remaining,
        });
      }
      toast.success(`Workspace "${unit.number}" removed successfully`);
      await loadConfigs();
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove workspace");
    }
  };

  const handleRemoveDepartmentFromFloorUnits = async (towerId, floorNumber, deptName, configIndex, fIndex) => {
    if (!window.confirm(`Are you sure you want to remove all "${deptName}" workspaces from ${formatFloorName(floorNumber)}?`)) return;
    try {
      const tower = configs[configIndex];
      const floor = tower.floorConfigs[fIndex];
      const deptUnits = floor.units.filter((u) => u.type === deptName);
      const flatIdsToRemove = deptUnits.map((u) => u.flat_id).filter(Boolean);

      if (flatIdsToRemove.length > 0) {
        await updateFlatStructure(flatIdsToRemove.map((fid) => ({ action: "REMOVE", flat_id: fid })));
      } else {
        const remaining = floor.units.filter((u) => u.type !== deptName);
        await updateFlatStructure({
          tower_id: towerId,
          floor_number: floorNumber,
          units: remaining,
        });
      }
      toast.success(`Removed "${deptName}" workspaces from ${formatFloorName(floorNumber)}`);
      await loadConfigs();
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove department workspaces");
    }
  };

  /* ================= SAVE FLOOR ================= */
  const handleSaveFloor = async (tIndex, fIndex) => {
    const tower = configs[tIndex];
    const floor = tower.floorConfigs[fIndex];
    const floorKey = `${tower.tower_id}_${floor.floor}`;

    if (isIndustrial && floor.units.length > 0) {
      const invalidUnits = floor.units.filter(
        (u) => !u.type || u.type === "—" || u.type === "Desk" || u.type === "Cabin" || !u.type.trim()
      );
      if (invalidUnits.length > 0) {
        toast.error(`Cannot save floor: ${invalidUnits.length} workspace(s) do not have a department assigned. Every desk/cabin must belong to a department.`);
        return;
      }
    }

    try {
      setSavingFloorKey(floorKey);

      await updateFlatStructure({
        tower_id: tower.tower_id,
        floor_number: floor.floor,
        units: floor.units,
      });

      toast.success(`${formatFloorName(floor.floor)} updated`);

      // Update original snapshot to reflect saved state so Cancel reverts to this saved state
      setOriginalFloorSnapshot((prevSnap) => ({
        ...prevSnap,
        [tower.tower_id]: {
          ...(prevSnap[tower.tower_id] || {}),
          [floor.floor]: JSON.parse(JSON.stringify(floor)),
        },
      }));

      await loadConfigs();
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to save floor");
    } finally {
      setSavingFloorKey(null);
    }
  };

  /* ================= REMOVE FLOOR ================= */
  const handleConfirmDeleteFloor = async () => {
    if (!floorToDelete) return;
    try {
      setDeletingFloor(true);
      await updateFlatStructure({
        tower_id: floorToDelete.towerId,
        floor_number: floorToDelete.floorNumber,
        units: [],
        delete_floor: true,
      });
      toast.success(`${formatFloorName(floorToDelete.floorNumber)} removed successfully`);
      setFloorToDelete(null);
      await loadConfigs();
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to remove floor");
    } finally {
      setDeletingFloor(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className={styles.container}>
      {/* HEADER / TYPE INFO */}
      <div className={styles.topInfoBar}>
        <div className={styles.topInfoLeft}>
          <span className={styles.structureBadge}>
            <Building2 size={14} />
            <span>{structureConfig.label}</span>
          </span>
          <span className={styles.infoDivider}>•</span>
          <span className={styles.unitsBadge}>
            <LayoutGrid size={13} />
            <span>{structureConfig.unitPlural}</span>
          </span>
        </div>

        {!readOnly && !isIndustrial && (
          <div className={styles.topInfoRight}>
            {showCustomTypeInput ? (
              <div className={styles.customTypeRow}>
                <TextInput
                  placeholder={`e.g. Executive ${structureConfig.unitLabel}`}
                  value={customTypeInput}
                  onChange={(e) => setCustomTypeInput(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                  style={{ width: "170px" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustomUnitType();
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={handleAddCustomUnitType}
                  disabled={savingCustomType || !customTypeInput.trim()}
                  loading={savingCustomType}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCustomTypeInput(false)}
                  title="Close"
                  icon={<X size={14} />}
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCustomTypeInput(true)}
                icon={<Plus size={14} />}
              >
                Add {structureConfig.unitLabel} Type
              </Button>
            )}
          </div>
        )}
      </div>

      {/* TABS (Render only when multiple towers/buildings exist) */}
      {configs.length > 1 && (
        <div className={styles.tabs}>
          {configs.map((t) => (
            <Button
              key={t.tower_id}
              variant={activeTowerId === t.tower_id ? "primary" : "outline"}
              size="sm"
              onClick={() => setActiveTowerId(t.tower_id)}
              icon={<Building size={14} />}
            >
              {t.tower_name}
            </Button>
          ))}
        </div>
      )}

      {/* ACTIVE TOWER */}
      {configs
        .filter((c) => c.tower_id === activeTowerId)
        .map((c) => {
          const configIndex = configs.findIndex(
            (t) => t.tower_id === c.tower_id
          );

          const existingFloorNumbers = c.floorConfigs.map((f) => f.floor);
          const hasGroundFloor = existingFloorNumbers.includes(0);
          const lowestFloor = existingFloorNumbers.length ? Math.min(...existingFloorNumbers) : 0;
          const nextBasementNumber = lowestFloor < 0 ? lowestFloor - 1 : -1;

          const totalUnitsCount = c.floorConfigs.reduce((acc, f) => acc + (f.units?.length || 0), 0);

          return (
            <div key={c.tower_id} className={styles.towerCard}>
              {/* ASMITA THEME HERO BANNER */}
              <div className={styles.heroCard}>
                <div className={styles.heroLeft}>
                  <div className={styles.heroIconCircle}>
                    <Building2 size={26} color="#ffffff" />
                  </div>
                  <div>
                    <div className={styles.heroName}>{c.tower_name}</div>
                    <div className={styles.heroRole}>
                      <Shield size={13} />
                      <span>{structureConfig.label} Structure</span>
                      <span style={{ opacity: 0.85 }}>
                        • {c.floorConfigs.length} {c.floorConfigs.length === 1 ? structureConfig.levelLabel : `${structureConfig.levelLabel}s`}
                      </span>
                      <span style={{ opacity: 0.85 }}>
                        • {totalUnitsCount} {totalUnitsCount === 1 ? shortUnitLabel : shortUnitPlural}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* FLOORS */}
              {c.floorConfigs.length > 0 ? (
                <div className={styles.floorsList}>
                  {c.floorConfigs.map((floor, fIndex) => {
                    const expanded = isExpanded(c.tower_id, floor.floor);
                    const isEditing = isEditable(c.tower_id, floor.floor);
                    const isDeptUnit = shortUnitLabel === "Dept" || shortUnitLabel === "Department";
                    const badgeText = isDeptUnit
                      ? `${floor.units.length} Dept`
                      : `${floor.units.length} ${floor.units.length === 1 ? shortUnitLabel : shortUnitPlural}`;

                    return (
                      <div
                        key={fIndex}
                        className={[
                          styles.floorCard,
                          expanded ? styles.floorCardExpanded : ""
                        ].filter(Boolean).join(" ")}
                      >
                        {/* FLOOR HEADER */}
                        <div
                          className={styles.floorHeader}
                          onClick={() => toggleFloorExpand(c.tower_id, floor.floor)}
                        >
                          <div className={styles.floorHeaderLeft}>
                            <div className={styles.chevronWrap}>
                              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            </div>
                            <span className={styles.floorTitle}>
                              {formatFloorName(floor.floor)}
                            </span>
                            <div className={styles.floorBadges}>
                              <span className={styles.countChip}>
                                <Layers size={11} strokeWidth={2} />
                                {badgeText}
                              </span>
                            </div>
                          </div>

                          <div className={styles.floorHeaderActions}>
                            <Button
                              type="button"
                              variant={isEditing ? "outline" : "ghost"}
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!expanded) toggleFloorExpand(c.tower_id, floor.floor);
                                toggleFloorEdit(c.tower_id, floor.floor, configIndex, fIndex);
                              }}
                              title={isEditing ? "Cancel Editing" : `Edit Floor ${shortUnitPlural}`}
                              icon={isEditing ? <X size={13} strokeWidth={2.2} /> : <Pencil size={13} strokeWidth={2} />}
                            >
                              {isEditing ? "Cancel" : "Edit Floor"}
                            </Button>
                            {!readOnly && (
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFloorToDelete({
                                    towerId: c.tower_id,
                                    floorNumber: floor.floor,
                                    unitCount: floor.units.length,
                                  });
                                }}
                                title="Remove Floor"
                                icon={<Trash2 size={13} />}
                              >
                                Remove Floor
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* FLOOR BODY (EXPANDED DETAILS) */}
                        {expanded && (
                          <div className={styles.floorBody}>
                            {isIndustrial ? (
                              <div className={styles.industrialFloorBody}>
                                {floor.units.length === 0 ? (
                                  <div className={styles.emptyWorkspaceCard}>
                                    <div className={styles.emptyWorkspaceIconWrap}>
                                      <LayoutGrid size={24} color="var(--color-dark-bg)" />
                                    </div>
                                    <div className={styles.emptyWorkspaceTitle}>
                                      No workspaces on {formatFloorName(floor.floor)}
                                    </div>
                                    <p className={styles.emptyWorkspaceDesc}>
                                      Set up open floor desks or private cabins for this level.
                                    </p>
                                    <Button
                                      type="button"
                                      variant="primary"
                                      size="sm"
                                      onClick={() => {
                                        if (!isEditing) {
                                          toggleFloorEdit(c.tower_id, floor.floor, configIndex, fIndex);
                                        }
                                        openWorkspaceModal(configIndex, fIndex, floor.floor);
                                      }}
                                      icon={<Plus size={15} />}
                                    >
                                      Add Workspaces (Cabins / Desks)
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                  {(() => {
                                    const floorDepts = Array.from(new Set(floor.units.map((u) => u.type).filter((t) => t && t !== "—")));
                                    if (!floorDepts.length) return null;
                                    return (
                                      <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" }}>
                                        <span style={{ fontSize: "12px", color: "var(--color-muted)", fontWeight: 600 }}>Departments:</span>
                                        {floorDepts.map((dName) => (
                                          <span
                                            key={dName}
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "6px",
                                              background: "#eff6ff",
                                              color: "#1d4ed8",
                                              border: "1px solid #bfdbfe",
                                              padding: "3px 10px",
                                              borderRadius: "14px",
                                              fontSize: "11.5px",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {dName}
                                            {!readOnly && (
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveDepartmentFromFloorUnits(c.tower_id, floor.floor, dName, configIndex, fIndex)}
                                                style={{
                                                  background: "transparent",
                                                  border: "none",
                                                  cursor: "pointer",
                                                  color: "#dc2626",
                                                  padding: "0 2px",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                }}
                                                title={`Remove ${dName} Department & its workspaces from this floor`}
                                              >
                                                <X size={12} />
                                              </button>
                                            )}
                                          </span>
                                        ))}
                                      </div>
                                    );
                                  })()}
                                  <div className={styles.workspaceGrid}>
                                    {floor.units.map((u, uIndex) => {
                                      const isCabin =
                                        String(u.number).includes("C-") ||
                                        String(u.type || "").toLowerCase().includes("cabin");
                                      return (
                                        <div key={uIndex} className={styles.workspaceCard}>
                                          <div className={styles.workspaceCardHeader}>
                                            <div className={styles.workspaceCardIcon}>
                                              {isCabin ? (
                                                <Building2 size={13} color="var(--color-dark-bg)" />
                                              ) : (
                                                <LayoutGrid size={13} color="var(--color-dark-bg)" />
                                              )}
                                            </div>
                                            <span className={styles.workspaceCardNumber}>{u.number}</span>
                                            {!readOnly && (
                                              <button
                                                type="button"
                                                className={styles.workspaceCardRemove}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (isEditing) {
                                                    handleRemoveUnit(configIndex, fIndex, uIndex);
                                                  } else {
                                                    handleDirectDeleteUnit(c.tower_id, floor.floor, u, configIndex, fIndex, uIndex);
                                                  }
                                                }}
                                                title={`Remove ${u.number}`}
                                              >
                                                <X size={13} />
                                              </button>
                                            )}
                                          </div>
                                          <div className={styles.workspaceCardType}>
                                            {isEditing ? (
                                              <SelectDropdown
                                                value={u.type}
                                                options={unitOptions
                                                  .filter((o) => o && o !== "—" && o !== "Desk" && o !== "Cabin" && o !== "Unit" && o !== "Flat" && o !== "None")
                                                  .map((o) => ({ label: `${o} Dept`, value: o }))}
                                                placeholder="Select Department..."
                                                isSearchable={false}
                                                isClearable={false}
                                                onChange={(e) =>
                                                  handleUnitChange(
                                                    configIndex,
                                                    fIndex,
                                                    uIndex,
                                                    e?.target ? e.target.value : (e?.value ?? e ?? "")
                                                  )
                                                }
                                              />
                                            ) : (
                                              <span className={styles.workspaceDeptBadge}>
                                                {u.type && u.type !== "—"
                                                  ? u.type
                                                  : isCabin
                                                    ? "Cabin"
                                                    : "Desk"}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  </>
                                )}

                                {isEditing && (
                                  <div className={styles.saveRow}>
                                    <Button
                                      type="button"
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => openWorkspaceModal(configIndex, fIndex, floor.floor)}
                                      icon={<Plus size={15} />}
                                    >
                                      Add Workspaces (Cabins / Desks)
                                    </Button>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => toggleFloorEdit(c.tower_id, floor.floor, configIndex, fIndex)}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="primary"
                                        loading={savingFloorKey === `${c.tower_id}_${floor.floor}`}
                                        disabled={Boolean(savingFloorKey)}
                                        onClick={() => handleSaveFloor(configIndex, fIndex)}
                                        style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                                      >
                                        <Check size={16} /> Save Floor Changes
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              /* RESIDENTIAL / COMMERCIAL ORIGINAL FLOOR BODY */
                              <>
                                <div className={styles.controlsGrid}>
                                  <div className={styles.controlField}>
                                    <label className={styles.controlLabel}>
                                      Default Type
                                    </label>
                                    <SelectDropdown
                                      value={floor.defaultType}
                                      options={unitOptions}
                                      disabled={!isEditing}
                                      isSearchable={false}
                                      isClearable={false}
                                      onChange={(e) =>
                                        handleFloorTypeChange(configIndex, fIndex, e?.target ? e.target.value : (e?.value ?? e ?? ""))
                                      }
                                    />
                                  </div>

                                  <div className={styles.controlFieldNarrow}>
                                    <label className={styles.controlLabel}>
                                      Total {shortUnitPlural}
                                    </label>
                                    <TextInput
                                      type="number"
                                      min="0"
                                      max={MAX_UNITS}
                                      value={floor.unitCount}
                                      disabled={!isEditing}
                                      onChange={(e) =>
                                        handleUnitCountChange(configIndex, fIndex, e?.target ? e.target.value : (e?.value ?? e ?? ""))
                                      }
                                    />
                                  </div>
                                </div>

                                <div className={styles.unitsGrid}>
                                  {floor.units.map((u, uIndex) => (
                                    <div key={uIndex} className={styles.unitRow}>
                                      <span className={styles.unitNumber}>{u.number}</span>
                                      <div style={{ flex: 1 }}>
                                        <SelectDropdown
                                          value={u.type}
                                          options={unitOptions}
                                          disabled={!isEditing}
                                          isSearchable={false}
                                          isClearable={false}
                                          placeholder={`Select ${shortUnitLabel}...`}
                                          onChange={(e) =>
                                            handleUnitChange(configIndex, fIndex, uIndex, e?.target ? e.target.value : (e?.value ?? e ?? ""))
                                          }
                                        />
                                      </div>
                                      {isEditing && (
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          style={{ color: "var(--color-danger)" }}
                                          onClick={() => handleRemoveUnit(configIndex, fIndex, uIndex)}
                                          title={`Remove ${shortUnitLabel}`}
                                          icon={<Trash2 size={15} />}
                                        />
                                      )}
                                    </div>
                                  ))}
                                  {floor.units.length === 0 && (
                                    <p style={{ margin: "8px 0", fontSize: "13px", color: "#64748b", fontStyle: "italic" }}>
                                      No {shortUnitPlural.toLowerCase()} on this floor. Use &quot;Add {shortUnitLabel}&quot; or save to keep 0.
                                    </p>
                                  )}
                                </div>

                                {isEditing && (
                                  <div className={styles.saveRow}>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleAddUnit(configIndex, fIndex)}
                                      icon={<Plus size={15} />}
                                    >
                                      Add {shortUnitLabel}
                                    </Button>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => toggleFloorEdit(c.tower_id, floor.floor, configIndex, fIndex)}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="primary"
                                        loading={savingFloorKey === `${c.tower_id}_${floor.floor}`}
                                        disabled={Boolean(savingFloorKey)}
                                        onClick={() => handleSaveFloor(configIndex, fIndex)}
                                        style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                                      >
                                        <Check size={16} /> Save Floor Changes
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* ADD FLOOR BUTTONS */}
                  {!readOnly && (
                    <div className={styles.addFloorBar}>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addFloor(configIndex)}
                        icon={<Plus size={15} />}
                      >
                        Add Floor
                      </Button>

                      {!hasGroundFloor && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addFloor(configIndex, 0)}
                          title="Add Ground Floor (Floor 0)"
                          icon={<ArrowDownToLine size={15} />}
                        >
                          Add Ground Floor (G)
                        </Button>
                      )}

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addFloor(configIndex, nextBasementNumber)}
                        title={`Add Basement ${Math.abs(nextBasementNumber)}`}
                        icon={<Layers size={15} />}
                      >
                        Add Basement ({`B${Math.abs(nextBasementNumber)}`})
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.card}>
                  <div className={styles.emptyCardHeader}>
                    <div className={styles.emptyCardIconCircle}>
                      <Layers size={24} color="var(--color-dark-bg)" />
                    </div>
                    <div>
                      <div className={styles.emptyTitle}>
                        {isIndustrial
                          ? `Configure Workplace Structure for ${c.tower_name}`
                          : `Configure Structure for ${c.tower_name}`}
                      </div>
                      <p className={styles.emptyHint}>
                        {isIndustrial
                          ? "Define floor levels to initialize workspaces. Each floor can have different workspace and cabin setups."
                          : `Configure ${structureConfig.label} specifications and generate units.`}
                      </p>
                    </div>
                  </div>

                  <div className={styles.configGrid}>
                    <div className={styles.fieldBox}>
                      <label className={styles.fieldLabel}>Total Floors / Levels</label>
                      <TextInput
                        value={c.total_floors}
                        disabled={readOnly}
                        placeholder="e.g. 5"
                        onChange={(e) =>
                          handleTowerConfigChange(configIndex, "total_floors", e?.target ? e.target.value : (e?.value ?? e ?? ""))
                        }
                      />
                    </div>

                    <div className={styles.fieldBox}>
                      <label className={styles.fieldLabel}>
                        {isIndustrial ? "Initial Workspaces per Floor (Optional)" : "Units per Floor"}
                      </label>
                      <TextInput
                        value={c.units_per_floor}
                        disabled={readOnly}
                        placeholder={isIndustrial ? "e.g. 0 (configure per floor)" : "e.g. 4"}
                        onChange={(e) =>
                          handleTowerConfigChange(configIndex, "units_per_floor", e?.target ? e.target.value : (e?.value ?? e ?? ""))
                        }
                      />
                    </div>

                    <div className={styles.fieldBox}>
                      <label className={styles.fieldLabel}>Start Floor</label>
                      <SelectDropdown
                        value={c.start_floor ?? structureConfig.defaultStartFloor ?? 1}
                        disabled={readOnly}
                        isSearchable={false}
                        isClearable={false}
                        options={[
                          { label: "Ground Floor (Floor 0)", value: 0 },
                          { label: "1st Floor (Floor 1)", value: 1 },
                          { label: "Basement 1 (Floor -1)", value: -1 },
                        ]}
                        onChange={(e) =>
                          handleTowerConfigChange(configIndex, "start_floor", Number(e?.target ? e.target.value : (e?.value ?? e ?? 0)))
                        }
                      />
                    </div>

                    {!isIndustrial && (
                      <div className={styles.fieldBox}>
                        <label className={styles.fieldLabel}>
                          Default Unit Type
                        </label>
                        <SelectDropdown
                          value={c.default_unit_type || defaultUnitType || ""}
                          disabled={readOnly}
                          isSearchable={false}
                          isClearable={false}
                          options={unitOptions}
                          onChange={(e) =>
                            handleTowerConfigChange(configIndex, "default_unit_type", e?.target ? e.target.value : (e?.value ?? e ?? ""))
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.actionsRow}>
                    <Button
                      disabled={readOnly || loading || !isGenerateValid(c)}
                      loading={loading}
                      onClick={() => handleGenerateTowerUnits(configIndex)}
                    >
                      {isIndustrial ? "Generate Workspaces" : "Generate Units"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {/* CONFIRM DELETE FLOOR MODAL */}
      <Modal
        isOpen={Boolean(floorToDelete)}
        onClose={() => !deletingFloor && setFloorToDelete(null)}
        title="Remove Floor"
        size="sm"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <p style={{ margin: 0, fontSize: "14px", color: "#475569", lineHeight: 1.5 }}>
            Are you sure you want to remove{" "}
            <strong style={{ color: "#0f172a" }}>
              {floorToDelete ? formatFloorName(floorToDelete.floorNumber) : "this floor"}
            </strong>
            {floorToDelete?.unitCount > 0 ? (
              <>
                {" "}and all its{" "}
                <strong>
                  {floorToDelete.unitCount} {floorToDelete.unitCount === 1 ? shortUnitLabel : shortUnitPlural}
                </strong>
              </>
            ) : null}
            ? This will deactivate the units on this floor and remove it from the structure.
          </p>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <Button
              variant="outline"
              disabled={deletingFloor}
              onClick={() => setFloorToDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deletingFloor}
              onClick={handleConfirmDeleteFloor}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <Trash2 size={15} />
              <span>Remove Floor</span>
            </Button>
          </div>
        </div>
      </Modal>

      {/* UNIFIED WORKSPACE MODAL (CABIN / DESK WITH BULK TOGGLE) */}
      <Modal
        isOpen={workspaceModalOpen}
        onClose={() => setWorkspaceModalOpen(false)}
        title={targetFloorForWorkspace ? `Add Workspaces to ${formatFloorName(targetFloorForWorkspace.floorNumber)}` : "Add Workspaces"}
        size="md"
      >
        <div className={styles.workspaceModal}>
          {/* Space Type Selector (Cabin vs Desk) */}
          <div>
            <label className={styles.fieldLabel} style={{ marginBottom: "6px" }}>Workspace Category</label>
            <div className={styles.workspaceTypeSelector}>
              <button
                type="button"
                className={`${styles.workspaceTypeBtn} ${workspaceType === "desk" ? styles.workspaceTypeBtnActive : ""}`}
                onClick={() => {
                  setWorkspaceType("desk");
                  setWorkspacePayload(prev => ({ ...prev, prefix: "D-" }));
                }}
              >
                <LayoutGrid size={15} />
                <span>Open Floor Desk</span>
              </button>
              <button
                type="button"
                className={`${styles.workspaceTypeBtn} ${workspaceType === "cabin" ? styles.workspaceTypeBtnActive : ""}`}
                onClick={() => {
                  setWorkspaceType("cabin");
                  setWorkspacePayload(prev => ({ ...prev, prefix: "C-" }));
                }}
              >
                <Building2 size={15} />
                <span>Cabin / Suite</span>
              </button>
            </div>
          </div>

          {/* Single vs Bulk Mode Toggle Switch */}
          <div className={styles.bulkToggleCard}>
            <div className={styles.bulkToggleLeft}>
              <span className={styles.bulkToggleTitle}>
                {bulkMode ? "Bulk Generation Mode" : "Singular Mode"}
              </span>
              <span className={styles.bulkToggleSubtitle}>
                {bulkMode
                  ? `Generate multiple ${workspaceType === "cabin" ? "cabins" : "desks"} in one go`
                  : `Add a single ${workspaceType === "cabin" ? "cabin" : "desk"} with custom details`}
              </span>
            </div>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={bulkMode}
                onChange={(e) => setBulkMode(e.target.checked)}
              />
              <span className={styles.toggleSlider}></span>
            </label>
          </div>

          {/* Form Fields */}
          <div className={styles.workspaceFieldsGrid}>
            {!bulkMode ? (
              <div style={{ gridColumn: "1 / -1" }}>
                <label className={styles.fieldLabel}>
                  Auto-Generated {workspaceType === "cabin" ? "Cabin Code" : "Desk Code"}
                </label>
                <div className={styles.lockedCodeBox}>
                  <Lock size={14} color="var(--color-dark-bg)" />
                  <span className={styles.lockedCodeText}>{livePreviewCode}</span>
                  <span className={styles.lockedCodeBadge}>System Generated</span>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className={styles.fieldLabel}>
                    Number of {workspaceType === "cabin" ? "Cabins" : "Desks"}
                  </label>
                  <TextInput
                    type="number"
                    min="1"
                    max="50"
                    placeholder="e.g. 5"
                    value={workspacePayload.count}
                    onChange={(e) => setWorkspacePayload(prev => ({ ...prev, count: Math.max(1, Number(e?.target ? e.target.value : (e?.value ?? e ?? 1))) }))}
                  />
                </div>
                <div>
                  <label className={styles.fieldLabel}>Auto-Generated Range</label>
                  <div className={styles.lockedCodeBox}>
                    <Lock size={14} color="var(--color-dark-bg)" />
                    <span className={styles.lockedCodeText}>{livePreviewRange}</span>
                  </div>
                </div>
              </>
            )}

            {workspaceType === "cabin" && (
              <div>
                <label className={styles.fieldLabel}>Desks inside Cabin</label>
                <TextInput
                  type="number"
                  min="1"
                  max="20"
                  value={workspacePayload.desksPerCabin}
                  onChange={(e) => setWorkspacePayload(prev => ({ ...prev, desksPerCabin: Math.max(1, Number(e?.target ? e.target.value : (e?.value ?? e ?? 1))) }))}
                />
              </div>
            )}

            <div>
              <label className={styles.fieldLabel}>Seats per Desk</label>
              <TextInput
                type="number"
                min="1"
                max="20"
                value={workspacePayload.seatsPerDesk}
                onChange={(e) => setWorkspacePayload(prev => ({ ...prev, seatsPerDesk: Math.max(1, Number(e?.target ? e.target.value : (e?.value ?? e ?? 1))) }))}
              />
            </div>

            <div>
              <label className={styles.fieldLabel}>
                Department <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <SelectDropdown
                value={workspacePayload.department}
                options={unitOptions
                  .filter(opt => opt && opt !== "—" && opt !== "Desk" && opt !== "Cabin" && opt !== "Unit" && opt !== "Flat" && opt !== "None")
                  .map(opt => ({ label: `${opt} Department`, value: opt }))}
                placeholder="Select Department (Required)..."
                isSearchable={true}
                isClearable={false}
                onChange={(e) => setWorkspacePayload(prev => ({ ...prev, department: e?.target ? e.target.value : (e?.value ?? e ?? "") }))}
              />
              {!workspacePayload.department && (
                <span style={{ fontSize: "11px", color: "#ef4444", marginTop: "4px", display: "block" }}>
                  Every desk/cabin must belong to a department.
                </span>
              )}
            </div>
          </div>

          {/* Helpful Preview Summary */}
          <div className={styles.workspaceSummaryBox}>
            <strong>Preview: </strong>
            {bulkMode ? (
              <span>Will auto-generate {workspacePayload.count} {workspaceType === "cabin" ? "cabins" : "desks"} ({livePreviewRange}) with department <strong>{workspacePayload.department || "General"}</strong>.</span>
            ) : (
              <span>Will add {workspaceType === "cabin" ? "cabin" : "desk"} with auto-generated code <strong>{livePreviewCode}</strong>.</span>
            )}
          </div>

          {/* Action Buttons */}
          <div className={styles.modalFooterActions}>
            <Button
              variant="outline"
              onClick={() => setWorkspaceModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isIndustrial && (!workspacePayload.department || !workspacePayload.department.trim())}
              onClick={handleApplyWorkspaces}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <Plus size={15} />
              <span>{bulkMode ? `Generate ${workspacePayload.count} ${workspaceType === "cabin" ? "Cabins" : "Desks"}` : `Add ${workspaceType === "cabin" ? "Cabin" : "Desk"}`}</span>
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UnitForm;
